import { useState } from "react";
import "./legal-onboarding-frame.css";

/**
 * Unified Legal & Policy Onboarding Frame — single scrollable acceptance panel.
 * Inserted after payment details and before account creation / redirect.
 */
export default function LegalOnboardingFrame({ onAccept }) {
  const [agreed, setAgreed] = useState(false);

  function handleAccept() {
    if (!agreed) return;
    onAccept?.();
  }

  return (
    <div className="legal-onboarding-frame">
      <header className="legal-onboarding-frame__header">
        <h2>Legal &amp; Policy Acceptance</h2>
        <p>Please review the agreements below, then accept to continue.</p>
      </header>

      <div className="legal-onboarding-frame__scroll" tabIndex={0}>
        <section>
          <h3>1. License Agreement</h3>
          <p>
            Subject to your timely payment of applicable fees and your acceptance of these terms,
            DreamHome Journey / REChecklist (“the Platform”) grants you a limited, non-exclusive,
            non-transferable, revocable license to access and use the Platform solely for your
            lawful real estate professional or client workflow purposes.
          </p>
          <p>
            <strong>Usage rights.</strong> You may use the Platform features enabled for your plan
            (including buyer and seller checklists, messaging, exports, and desk tools) for
            authorized users under your account.
          </p>
          <p>
            <strong>Restrictions.</strong> You may not sublicense, sell, rent, lease, reverse
            engineer, decompile, or create derivative works of the Platform except as permitted by
            law. You may not remove proprietary notices or circumvent access controls.
          </p>
          <p>
            <strong>Intellectual property.</strong> The Platform, including its software, copy,
            workflows, visual design, and documentation, remains the exclusive property of the
            Platform operator and its licensors. No ownership interest is transferred by this
            license.
          </p>
          <p>
            <strong>Permitted use.</strong> Use consistent with your subscription tier, applicable
            law, and professional standards for real estate practice.
          </p>
          <p>
            <strong>Prohibited use.</strong> Any use that infringes rights, violates law, harms
            Platform integrity, or misrepresents affiliation with DreamHome Journey / REChecklist.
          </p>
        </section>

        <section>
          <h3>2. End User License Agreement (EULA)</h3>
          <p>
            By creating or using an account, you agree to this End User License Agreement. Access is
            conditioned on continued compliance.
          </p>
          <p>
            <strong>Acceptable use.</strong> You will use the Platform honestly, lawfully, and in a
            manner that respects clients, colleagues, and the Platform community.
          </p>
          <p>
            <strong>Prohibited actions.</strong> You will not attempt unauthorized access, disrupt
            service, upload malware, scrape protected content at scale, impersonate others, or use
            the Platform to harass, defraud, or unlawfully solicit.
          </p>
          <p>
            <strong>Account responsibilities.</strong> You are responsible for safeguarding
            credentials, for activity under your account, and for keeping contact and billing
            information accurate.
          </p>
          <p>
            <strong>Data handling.</strong> You will handle client and office information according
            to applicable privacy and professional obligations. Local device storage and exports
            remain under your care.
          </p>
          <p>
            <strong>Platform behavior expectations.</strong> The Platform is a decision-making and
            workflow companion. It is not a marketplace, listing exchange, legal advisor, or
            substitute for licensed professional judgment.
          </p>
        </section>

        <section>
          <h3>3. Non‑Disclosure Statement (End‑User Focused)</h3>
          <p>
            You acknowledge that the Platform contains confidential and proprietary information. You
            agree not to disclose, publish, or misuse such information except as needed to use the
            Platform for its intended purpose or as required by law.
          </p>
          <p>
            Confidential information includes, without limitation: Platform features and roadmaps;
            workflows and process design; proprietary UI/UX patterns and presentation; internal
            operational processes; and non-public agent, broker, office, or client data you access
            through the Platform.
          </p>
          <p>
            These confidentiality obligations survive account closure to the extent the information
            remains non-public and proprietary.
          </p>
        </section>

        <section>
          <h3>4. Buyer/Seller Statements</h3>
          <p>
            If you use the Platform as a buyer or seller client, you understand it is provided to
            support clarity and collaboration with your agent or broker. You remain responsible for
            decisions you make about property, financing, and transactions.
          </p>
          <p>
            <strong>Privacy considerations.</strong> Information you enter may be shared with your
            invited agent or broker as part of the guided journey. Review what you submit before
            sending.
          </p>
          <p>
            <strong>Data export expectations.</strong> Export any records you wish to keep before
            ending access. Device-local copies are your responsibility to manage.
          </p>
          <p>
            <strong>Communication expectations.</strong> Messaging and submissions are tools for
            professional coordination; respond promptly and courteously when participating.
          </p>
        </section>

        <section>
          <h3>5. Broker/Agent Statements</h3>
          <p>
            Licensed professionals using the Platform agree to maintain professional conduct,
            supervise assigned seats where applicable, and handle client data with care consistent
            with brokerage policy and law.
          </p>
          <p>
            <strong>Client data handling.</strong> Limit access to those who need it; do not use
            Platform data for unrelated marketing lists or unauthorized disclosure.
          </p>
          <p>
            <strong>Office-level responsibilities.</strong> Brokers are responsible for seat
            assignment, oversight of messaging/archive practices enabled for their office, and
            ensuring users under their plan understand these terms.
          </p>
          <p>
            <strong>Compliance expectations.</strong> The Platform does not certify DRE compliance
            and does not provide legal opinions. You remain responsible for your regulatory duties.
          </p>
          <p>
            <strong>Billing rules.</strong> Paid desks renew according to the selected monthly or
            annual cycle. Seat changes and plan changes follow the checkout terms shown at purchase.
          </p>
          <p>
            <strong>Cancellation rules.</strong> Cancellation follows the Cancellation Policy below,
            including notice timing relative to renewal.
          </p>
        </section>

        <section>
          <h3>6. Cancellation Policy</h3>
          <p>Users may cancel at any time.</p>
          <p>No refunds are provided.</p>
          <p>A 72-hour buffer exists before renewal.</p>
          <p>Users are responsible for exporting their data before cancellation.</p>
          <p>All user data is deleted upon account closure.</p>
        </section>

        <section>
          <h3>7. Payment Terms</h3>
          <p>
            Paid plans are billed in advance on a monthly or annual cycle as selected at checkout.
            Renewals occur automatically at the then-current rate for your plan unless cancelled
            with sufficient notice under the Cancellation Policy.
          </p>
          <p>
            Failed payments may result in retry attempts, temporary access limitation, or account
            suspension until payment is restored. You authorize the Platform’s payment processor
            (including PayPal where used) to charge the selected amount for the chosen cycle.
          </p>
          <p>
            Taxes, currency conversion, and processor fees may apply as presented by the payment
            provider. Plan features remain subject to the License Agreement and your tier.
          </p>
        </section>

        <section>
          <h3>8. Liability &amp; Warranty Disclaimer</h3>
          <p>
            The Platform is provided “as-is” and “as available,” without warranties of any kind,
            express or implied, including merchantability, fitness for a particular purpose, and
            non-infringement, to the fullest extent permitted by law.
          </p>
          <p>
            To the fullest extent permitted by law, the Platform operator is not liable for misuse
            of the Platform; third-party actions or outages (including payment processors and
            hosting); or decisions made by agents, brokers, buyers, or sellers.
          </p>
          <p>
            Nothing in the Platform constitutes legal, financial, or brokerage advice. You use the
            Platform at your own professional and personal risk.
          </p>
        </section>

        <section>
          <h3>9. Data Ownership Statement</h3>
          <p>Your data belongs to you.</p>
          <p>We do not sell or share your data.</p>
          <p>We do not retain your data after closure.</p>
          <p>You are responsible for exporting your data.</p>
        </section>
      </div>

      <div className="legal-onboarding-frame__accept">
        <label className="legal-onboarding-frame__check">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
          />
          <span>
            I have read and agree to the License Agreement, End User Agreement, Non‑Disclosure
            Statement, and all policies contained herein.
          </span>
        </label>
        <button
          type="button"
          className="legal-onboarding-frame__cta"
          disabled={!agreed}
          onClick={handleAccept}
        >
          I Accept and Continue
        </button>
      </div>
    </div>
  );
}
